import React, { useState, useMemo } from "react";
import {v4 as uuidv4} from 'uuid'
import styled from "styled-components";
import { darken, lighten } from "polished";
import Select from "./common/Select";
import Button from "./common/Button";
import Field from "./common/Field";
import Label from "./common/Label";
import Input from "./common/Input";
import t from "./locales";
import { colors } from "../styles/variables";
import './styles/price_calculator'
import { CURRENT_CART_VERSION } from '../ShoppingCart/constants';
import { ICartItems, IJRWTicketBase, IJRWTicketOrderItem } from '../ShoppingCart/types';
import {IPlanJRW} from '../PriceCalculator/types'
import { retrieveCartItems, storeCartItems } from '../ShoppingCart/helpers';

const syncCart = () => {
  return new Promise<void>((resolve) => {
    setTimeout(() => { resolve(); }, 1000);
  });
};

interface IProps {
  plans: IPlanJRW[];
  ticket: IJRWTicketBase
}

interface IField {
  id: string
  value: string
  isRequired: boolean
  valueMissing: boolean
  patternMismatch: boolean
}

const StyledDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1rem;
  div {
    width: 48%;
    &:first-child {
      margin-right: 4%;
    }
  }
`;

const StyledLabel = styled(Label)`
  line-height: 20px;
  font-size: 0.8rem;
  display: inline-block;
  white-space: nowrap;
  margin-bottom: 0;
  margin-right: 0.5rem;
`;

const BoldLabel = styled(StyledLabel)`
  font-size: 1rem;
  font-weight: bold;
`;

const StyledButton = styled(Button)`
  border-radius: 4px;
  display: block;
  text-align: center;
  transition: 0.2s all ease-in-out;
  background: transparent;
  border: none;
`;

const TextButton = styled(StyledButton)`
  color: ${(props) => (props.disabled ? colors.gray600 : colors.red)};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  &:hover {
    color: ${(props) =>
      props.disabled ? colors.gray600 : darken(0.05, colors.red)};
  }
`;

const BorderButton = styled(StyledButton)`
  min-width: 100px;
  color: ${colors.red};
  background-color: ${colors.white};
  border: ${`1px solid ${colors.red}`};
  padding: 0.5rem 1rem;
  &:hover {
    color: ${colors.red};
    background-color: ${lighten(0.35, colors.red)};
  }
`;

const TrashButton = styled(StyledButton)`
  color: ${colors.gray600};
  &:hover {
    color: ${colors.gray800};
  }
`;

const NameFieldGroup = ({
  index,
  firstName,
  lastName,
  onClick,
  onChange,
  className
}: {
  index: number;
  firstName: IField
  lastName: IField
  onClick: () => void;
  onChange: (event: React.ChangeEvent) => void
  className: string
}) => {
  return (
    <div>
      <StyledDiv>
        <p style={{ marginBottom: 0, fontSize: "1rem", fontWeight: "bold" }}>
          {t.passenger()} {index}
        </p>
        <TrashButton onClick={onClick}>
          <i className="fas fa-trash" />
        </TrashButton>
      </StyledDiv>
      <StyledDiv>
        <Field>
          <div className='d-flex align-items-center mb-1'>
            <StyledLabel htmlFor={firstName.id}>
              {t.firstName()}
            </StyledLabel>
            {firstName.isRequired && <span className='text-required' style={{paddingTop: '0.1rem', paddingBottom: '0.1rem'}}>{t.required()}</span>}
          </div>
          <Input id={firstName.id} onChange={onChange} placeholder={t.firstNamePlaceholder()} required={firstName.isRequired} className={firstName.valueMissing || firstName.patternMismatch ? 'invalid' : ''} />
          <span className={`${firstName.valueMissing ? 'd-block' : 'd-none'} text-sm text-danger`}>{t.valueMissingError()}</span>
          <span className={`${firstName.patternMismatch ? 'd-block' : 'd-none'} text-sm text-danger`}>{t.patternMismatchError()}</span>
        </Field>
        <Field>
          <div className='d-flex align-items-center mb-1'>
            <StyledLabel htmlFor={lastName.id}>
              {t.lastName()}
            </StyledLabel>
            {lastName.isRequired && <span className='text-required' style={{paddingTop: '0.1rem', paddingBottom: '0.1rem'}}>{t.required()}</span>}
          </div>
          <Input id={lastName.id} onChange={onChange} placeholder={t.lastNamePlaceholder()} required={lastName.isRequired} className={lastName.valueMissing || lastName.patternMismatch ? 'invalid' : ''} />
          <span className={`${lastName.valueMissing ? 'd-block' : 'd-none'} text-sm text-danger`}>{t.valueMissingError()}</span>
          <span className={`${lastName.patternMismatch ? 'd-block' : 'd-none'} text-sm text-danger`}>{t.patternMismatchError()}</span>
        </Field>
      </StyledDiv>

    </div>
  );
};

const JRWTicketPriceCalculator = ({ plans, ticket }: IProps) => {
  const [passengers, setPassengers] = useState([
    {
      firstName: {id: uuidv4(), value: "", isRequired: true, valueMissing: false, patternMismatch: false},
      lastName: {id: uuidv4(), value: "", isRequired: true, valueMissing: false, patternMismatch: false}
    }
  ])
  const [className, setClassName] = useState('')
  const [selectedPlan, setSelectedPlan] = useState(plans[0])
  const [isLoading, setIsLoading] = useState(false)
  const url = new URL(window.location.href);

  const numPassengers = useMemo(() => {
    return passengers.filter(passenger => passenger.firstName.value !== "" && passenger.lastName.value !== "").length
  }, [passengers])

  const handleSelect = (event: React.ChangeEvent) => {
    const target = event.target as HTMLOptionElement
    const plan = plans.find(p => p.name === target.value)
    setSelectedPlan(plan)
  }

  const handleClickAddPassenger = () => {
    if (passengers.length === 10) return;
    setPassengers(prev => ([...prev, {firstName: {id: uuidv4(), value: "", isRequired: false, valueMissing: false, patternMismatch: false}, lastName: {id: uuidv4(), value: "", isRequired: false, valueMissing: false, patternMismatch: false}}]))
  };
  const handleClickRemovePassenger = () => {
    if (passengers.length === 1) return;
    setPassengers(prev => prev.slice(0, prev.length - 1))
  };
  const handleInputChange = (event: React.ChangeEvent) => {
    setClassName('')
    const target = event.target as HTMLInputElement
    setPassengers(passengers => passengers.map(passenger => {
      if (passenger.firstName.id === target.id) {
        return {...passenger, firstName: {...passenger.firstName, value: target.value, valueMissing: false, patternMismatch: false}}
      }
      if (passenger.lastName.id === target.id) {
        return {...passenger, lastName: {...passenger.lastName, value: target.value, valueMissing: false, patternMismatch: false}}
      }
      return passenger
    }))
  }
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (isLoading) return
    /**
     * TODO: validate user inputs
     */

    const newPassengers = passengers.map(passenger => {
      let newPassenger = passenger
      if (passenger.firstName.isRequired && passenger.firstName.value === '') {
        newPassenger = {...newPassenger, firstName: {...newPassenger.firstName, valueMissing: true}}
      }
      if (passenger.lastName.isRequired && passenger.lastName.value === '') {
        newPassenger =  {...newPassenger, lastName: {...newPassenger.lastName, valueMissing: true}}
      }
      if (passenger.firstName.value !== '' && !/[A-Za-zs]{1,}/.test(passenger.firstName.value)) {
        newPassenger = {...newPassenger, firstName: {...newPassenger.firstName, patternMismatch: true}}
      }
      if (passenger.lastName.value !== '' && !/[A-Za-zs]{1,}/.test(passenger.lastName.value)) {
        newPassenger = {...newPassenger, lastName: {...newPassenger.lastName, patternMismatch: true}}
      }
      return newPassenger
    })
    if (newPassengers.filter(passenger => passenger.firstName.valueMissing || passenger.firstName.patternMismatch || passenger.lastName.valueMissing || passenger.lastName.patternMismatch).length > 0) {
      setPassengers(newPassengers)
      return
    }

    setIsLoading(true)

    const prevCartItems: ICartItems = retrieveCartItems(CURRENT_CART_VERSION);

    const jrwTicketOrderItem: IJRWTicketOrderItem = {
      ...ticket,
      plan: selectedPlan,
      numPassengers
    }

    const cartItems = { ...prevCartItems, jrwTicketOrderItems: [
      ...(prevCartItems.jrwTicketOrderItems || []), jrwTicketOrderItem
    ] };

    await syncCart();
    storeCartItems(cartItems)
    setIsLoading(false)
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* plan select */}
      <div className="price-calculator__section">
        <Field>
          <BoldLabel htmlFor="plans">{t.price()}</BoldLabel>
          <div style={{ position: "relative" }}>
            <Select id="plans" name="plans" plans={plans} defaultValue={selectedPlan.name} onChange={handleSelect} />
            <i
              className="fas fa-chevron-down"
              style={{
                display: "inline",
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: colors.gray600,
              }}
            ></i>
          </div>
          <p
            style={{
              color: colors.gray600,
              fontSize: "0.9rem",
              marginTop: "3px",
            }}
          >
            {t.limit()}
          </p>
        </Field>
      </div>
      {/* name input groups */}
      <div className="price-calculator__section">
        {passengers.map(({firstName, lastName}, i) => {
          return (
            <NameFieldGroup
              key={firstName.id}
              firstName={firstName}
              lastName={lastName}
              index={i + 1}
              onClick={handleClickRemovePassenger}
              onChange={handleInputChange}
              className={className}
            />
          );
        })}
      </div>
      <div
        className="price-calculator__section"
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        <TextButton
          onClick={handleClickAddPassenger}
          disabled={passengers.length === 10}
        >
          <span>
            <i
              className="fa fa-plus"
              aria-hidden="true"
              style={{ marginRight: "1px" }}
            ></i>
            追加乘客
          </span>
        </TextButton>
      </div>
      {/* add-to-cart button */}
      <div className="price-calculator__cta">
        {/* snow-cart-button = gtmのカートインイベント用のクラス
        TODO: クラス以外の方法でトリガーする */}
        <BorderButton type="submit" className="snow-cart-button">{t.addToCart()}</BorderButton>
      </div>
    </form>
  );
};

export default JRWTicketPriceCalculator;
